import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useLocation, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import Text from 'core-system/Text'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import TextField from 'core-system/TextField'
import Divider from 'core-system/Divider'
import palette from 'core-system/Themes/palette'
import FleetLogoFullColor from 'core-system/FleetLogos/FleetLogoFullColor'
import { ApiServerUrl } from 'redux/config/services/Constants'
import { ButtonsContainer } from 'features/Users/components/LoginForm'
import LoginSignUpGraphicV2, {
  ImageLabel,
} from 'features/Users/components/LoginSignUpGraphicV2'
import axios from 'axios'

const Container = styled.div`
  display: flex;
  height: 100%;
`

const LogoContainer = styled.div`
  margin-bottom: 3rem;
`

const LeftContainer = styled.div`
  width: 50%;
  background-color: ${(props) => props.theme.palette.white};
  padding: 3rem 4rem;
  display: flex;
  position: relative;
  flex-direction: column;
  overflow: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${(props) => props.theme.breakpoints[2]}) {
    width: 100%;
  }
`

const FormContainer = styled.div`
  a {
    width: 100%;
    height: 100%;
  }
`

const FieldContainer = styled.div`
  margin-bottom: 1.5rem;
`

const StatusText = styled.p<{ error?: boolean }>`
  margin-top: 1rem;
  ${(props) => props.theme.typography.caption};
  font-size: ${(props) => (props.error ? '0.75rem' : '1rem')};
  color: ${(props) =>
    props.error
      ? props.theme.palette.error.red500
      : props.theme.palette.accent.teal500};
`

const RoleButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  margin-bottom: 1.5rem;
`

const ScopeList = styled.ul`
  margin: 0 0 1.5rem;
  padding-left: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const ActionButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (min-width: ${(props) => props.theme.breakpoints[2]}) {
    flex-direction: row;
  }
`

const SignedInRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background-color: ${(props) => props.theme.palette.grey.grey100};
`

type ConsentContext = {
  client_name: string
  resource?: string
  required_role?: 'employer' | 'employee'
  suggested_role?: 'employer' | 'employee'
  available_roles: Array<'employer' | 'employee'>
  authenticated: boolean
  user_email?: string | null
  scopes_supported: string[]
  scope_descriptions: Record<string, string>
  csrf_token: string
}

const MCPConsentView: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )
  const [context, setContext] = useState<ConsentContext | null>(null)
  const [role, setRole] = useState<'employer' | 'employee' | ''>('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const fetchContext = async (): Promise<ConsentContext> => {
    const response = await axios.get(`${ApiServerUrl}/mcp/consent/context/`, {
      params: {
        client_id: params.get('client_id'),
        resource: params.get('resource'),
      },
      withCredentials: true,
    })
    return response.data
  }

  useEffect(() => {
    if (!params.get('client_id')) {
      setError('Missing OAuth client_id')
      return
    }
    fetchContext()
      .then((data) => {
        setContext(data)
        if (
          data.suggested_role &&
          data.available_roles.includes(data.suggested_role)
        ) {
          setRole(data.suggested_role)
        } else if (data.available_roles?.length === 1) {
          setRole(data.available_roles[0])
        } else {
          setRole('')
        }
      })
      .catch(() => setError('Unable to load MCP consent details'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params])

  const handleLogin = async () => {
    if (!context) return
    setLoading(true)
    setError(null)
    try {
      await axios.post(
        `${ApiServerUrl}/mcp/consent/login/`,
        { email, password },
        {
          withCredentials: true,
          headers: { 'X-CSRFToken': context.csrf_token },
        }
      )
      const refreshed = await fetchContext()
      setContext(refreshed)
      if (
        refreshed.suggested_role &&
        refreshed.available_roles.includes(refreshed.suggested_role)
      ) {
        setRole(refreshed.suggested_role)
      } else if (refreshed.available_roles?.length === 1) {
        setRole(refreshed.available_roles[0])
      } else {
        setRole('')
      }
    } catch (e: any) {
      setError(e?.response?.data?.non_field_errors?.[0] || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async () => {
    if (!context) return
    if (!role) {
      setError('Choose whether to connect as an employer admin or employee')
      return
    }
    setLoading(true)
    setError(null)
    try {
      await axios.post(
        `${ApiServerUrl}/mcp/consent/prepare/`,
        {
          role,
          resource: params.get('resource'),
          client_id: params.get('client_id'),
          state: params.get('state') || '',
        },
        {
          withCredentials: true,
          headers: { 'X-CSRFToken': context.csrf_token },
        }
      )
      window.location.href = `${ApiServerUrl}/oauth/authorize/?${params.toString()}`
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Unable to approve MCP access')
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    if (!context) return
    setLoading(true)
    setError(null)
    try {
      await axios.post(`${ApiServerUrl}/mcp/consent/logout/`, null, {
        withCredentials: true,
        headers: { 'X-CSRFToken': context.csrf_token },
      })
      const refreshed = await fetchContext()
      setContext(refreshed)
      setRole('')
      setEmail('')
      setPassword('')
    } catch {
      setError('Unable to sign out')
    } finally {
      setLoading(false)
    }
  }

  const handleDeny = () => {
    const redirectUri = params.get('redirect_uri')
    const clientId = params.get('client_id')
    const state = params.get('state')
    if (!redirectUri || !clientId) {
      navigate('/login')
      return
    }
    window.location.href = `${ApiServerUrl}/mcp/consent/deny/?client_id=${encodeURIComponent(
      clientId
    )}&redirect_uri=${encodeURIComponent(redirectUri)}${
      state ? `&state=${encodeURIComponent(state)}` : ''
    }`
  }

  const canApprove =
    context?.authenticated &&
    role &&
    context.available_roles.includes(role) &&
    (!context.required_role || context.required_role === role)

  const roleMismatchMessage =
    context?.authenticated &&
    context.required_role &&
    !context.available_roles.includes(context.required_role)
      ? context.required_role === 'employee'
        ? 'This connection requires an employee account with an active commuter profile. Sign in with a different account, or connect to fleet-employer-staging instead.'
        : 'This connection requires an employer admin account. Sign in with a different account, or connect to fleet-employee-staging instead.'
      : null

  const RoleButton = ({
    value,
    label,
  }: {
    value: 'employer' | 'employee'
    label: string
  }) =>
    role === value ? (
      <PrimaryButton fullWidth onClick={() => setRole(value)}>
        {label}
      </PrimaryButton>
    ) : (
      <SecondaryButton fullWidth onClick={() => setRole(value)}>
        {label}
      </SecondaryButton>
    )

  const clientName = context?.client_name || 'An MCP client'

  return (
    <>
      <Helmet>
        <title>Connect MCP | Fleet</title>
      </Helmet>
      <Container>
        <LeftContainer>
          <LogoContainer>
            <FleetLogoFullColor height='40' width='170' />
          </LogoContainer>
          <FormContainer>
            <Text variant='h1' marginBottom='1.5rem' textColor={palette.black}>
              Connect Fleet to your chatbot
            </Text>
            <Text variant='action2' marginBottom='1.5rem' textColor={palette.black}>
              {clientName} is requesting read-only access to your Fleet data.
            </Text>

            {context && !context.authenticated && (
              <>
                <Divider margin='2rem 0' />
                <FieldContainer>
                  <TextField
                    label='Email*'
                    placeholder='Email'
                    type='email'
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </FieldContainer>
                <FieldContainer>
                  <TextField
                    label='Password*'
                    placeholder='Password'
                    type='password'
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </FieldContainer>
                {error && <StatusText error>{error}</StatusText>}
                <ButtonsContainer>
                  <PrimaryButton
                    fullWidth
                    onClick={handleLogin}
                    disabled={loading}
                    isLoading={loading}
                  >
                    Sign in
                  </PrimaryButton>
                </ButtonsContainer>
              </>
            )}

            {context?.authenticated && (
              <>
                <SignedInRow>
                  <Text variant='body2' textColor={palette.black}>
                    {context.user_email
                      ? `Signed in as ${context.user_email}`
                      : 'Signed in to your Fleet account'}
                  </Text>
                  <SecondaryButton onClick={handleLogout} disabled={loading}>
                    Log out
                  </SecondaryButton>
                </SignedInRow>

                {context.available_roles.length > 1 && (
              <>
                <Text variant='action2' marginBottom='1rem' textColor={palette.black}>
                  Who are you connecting as?
                </Text>
                <RoleButtonRow>
                  <RoleButton value='employer' label='Employer admin' />
                  <RoleButton value='employee' label='Employee' />
                </RoleButtonRow>
                <Divider margin='2rem 0' />
              </>
            )}

                {roleMismatchMessage && (
                  <StatusText error>{roleMismatchMessage}</StatusText>
                )}
                {!roleMismatchMessage && (
                  <>
                <Text variant='action2' marginBottom='1rem' textColor={palette.black}>
                  This will allow {clientName} to:
                </Text>
                <ScopeList>
                  {(context.scopes_supported || []).map((scope) => (
                    <li key={scope}>
                      <Text variant='body2'>
                        {context.scope_descriptions?.[scope] || scope}
                      </Text>
                    </li>
                  ))}
                </ScopeList>
                {error && <StatusText error>{error}</StatusText>}
                <ActionButtonRow>
                  <PrimaryButton
                    fullWidth
                    onClick={handleApprove}
                    disabled={loading || !canApprove}
                    isLoading={loading}
                  >
                    Allow read-only access
                  </PrimaryButton>
                  <SecondaryButton fullWidth onClick={handleDeny} disabled={loading}>
                    Deny
                  </SecondaryButton>
                </ActionButtonRow>
                  </>
                )}
              </>
            )}

            {!context && error && <StatusText error>{error}</StatusText>}
          </FormContainer>
        </LeftContainer>
        <LoginSignUpGraphicV2 imageLabel={ImageLabel.LOGIN} />
      </Container>
    </>
  )
}

export default MCPConsentView
